import { useEffect, useState } from 'react';

import { type VariantProp, ALL_BG_COLORS } from '../common/colors';

function normalizeColor(color: string): string | null {
  if (!color || color === 'transparent' || color === 'rgba(0, 0, 0, 0)') {
    return null;
  }
  // getComputedStyle returns rgb(r,g,b) or rgba(r,g,b,a)
  const rgb = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
  if (rgb) {
    const r = Number.parseInt(rgb[1], 10);
    const g = Number.parseInt(rgb[2], 10);
    const b = Number.parseInt(rgb[3], 10);
    return `#${[r, g, b].map((x) => x.toString(16).padStart(2, '0')).join('')}`.toUpperCase();
  }
  // Already hex
  if (color.startsWith('#')) {
    const hex = color.slice(1);
    if (hex.length === 3) {
      return `#${hex
        .split('')
        .map((c) => `${c}${c}`)
        .join('')
        .toUpperCase()}`;
    }
    return color.toUpperCase();
  }
  return null;
}

function colorDistance(a: string, b: string): number {
  const hexToRgb = (hex: string) => {
    const h = hex.replace('#', '');
    const n = Number.parseInt(h, 16);
    return [Math.floor(n / 65536) % 256, Math.floor(n / 256) % 256, n % 256];
  };
  const [r1, g1, b1] = hexToRgb(a);
  const [r2, g2, b2] = hexToRgb(b);
  return Math.sqrt((r1 - r2) ** 2 + (g1 - g2) ** 2 + (b1 - b2) ** 2);
}

function findClosestVariant(computedColor: string): VariantProp {
  const normalized = normalizeColor(computedColor);
  if (!normalized) return 'neutral';

  let closest: VariantProp = 'neutral';
  let minDist = Infinity;

  for (const { variant, colors } of ALL_BG_COLORS) {
    for (const knownColor of colors) {
      const known = normalizeColor(knownColor);
      if (!known) continue;
      const dist = colorDistance(normalized, known);
      if (dist < minDist) {
        minDist = dist;
        closest = variant;
      }
    }
  }

  // If no close match (e.g. custom color), use luminance for light vs dark shadow
  return closest;
}

export interface UseSurfaceVariantOptions {
  /**
   * Fallback when variant cannot be determined (e.g. before mount, transparent parents).
   */
  fallback?: VariantProp;
}

/**
 * Detects which variant/surface color an element is sitting on by walking up the DOM
 * and matching the first opaque background against known variant colors.
 *
 * For portaled overlays (dropdowns, tooltips), pass a ref to the **anchor/trigger**
 * element (the button or element the overlay is positioned relative to), not the
 * overlay itself - since the overlay may be in a different DOM branch.
 *
 * @example
 *   const triggerRef = useRef<HTMLButtonElement>(null);
 *   const variant = useSurfaceVariant(triggerRef);
 *   <DropdownMenu anchorRef={triggerRef} css={getVariantShadowStyles(variant)} />
 */
export function useSurfaceVariant(
  elementRef: React.RefObject<Element | null>,
  options?: UseSurfaceVariantOptions,
): VariantProp {
  const [variant, setVariant] = useState<VariantProp>(() => options?.fallback ?? 'neutral');

  useEffect(() => {
    const el = elementRef.current;
    if (!el) return undefined;

    const detect = () => {
      // Start from parent: the element may have its own background (e.g. a tile), and we want
      // to detect the surface it's sitting *on*, not its own background.
      let current: Element | null = el.parentElement;

      while (current) {
        const style = window.getComputedStyle(current);
        const bg = style.backgroundColor;

        const normalized = normalizeColor(bg);
        if (normalized) {
          setVariant(findClosestVariant(bg));
          return;
        }
        current = current.parentElement;
      }

      setVariant(options?.fallback ?? 'neutral');
    };

    detect();

    // Re-detect when element might have moved (e.g. portal re-parenting)
    const observer = new MutationObserver(detect);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => observer.disconnect();
  }, [elementRef, options?.fallback]);

  return variant;
}
